/**
 * The experiment plane's client (DESIGN.md §11.12.5). PLATFORM_OPS only — the server enforces it on
 * every endpoint; the portal only hides the entry point for everyone else.
 */

import { AuthError } from './auth';
import { authorizedFetch } from './cognito';

const API_BASE = '/api/v1';

export interface Counts {
  queued: number;
  running: number;
  done: number;
  failed: number;
  cancelled: number;
}

export interface FloorCheck {
  value: number;
  floor: number | null;
  ceiling: number | null;
  pass: boolean;
}

export interface Diff {
  a: number | null;
  b: number | null;
  delta: number | null;
  direction: 'up' | 'down' | 'same' | 'only_a' | 'only_b';
}

export interface Summary {
  counts?: Counts;
  public?: Record<string, FloorCheck>;
  floors_pass?: boolean;
  public_incomplete_suites?: string[];
  tierb?: Record<string, number>;
  real_whole?: Record<string, number>;
  timing?: Record<string, { minutes: number; band: 'green' | 'amber' | 'fail' }>;
  vs_baseline?: Record<string, Diff>;
  same_resolved_set?: boolean;
  finalize_error?: string;
  abandoned?: string;
}

export interface Experiment {
  id: string;
  status: 'QUEUED' | 'RUNNING' | 'DONE' | 'PARTIAL' | 'CANCELLED';
  component: string;
  purpose: 'experiment' | 'baseline' | 'promotion-check';
  created_at: string | null;
  config_hash: string;
  overrides: Record<string, unknown>;
  image_tag: string | null;
  git_sha: string | null;
  resolved_set: {
    suites: string[];
    datasets: string[];
    devices: string[];
    corpus_versions: Record<string, string>;
    ignored: string[];
    excluded: Record<string, string[]>;
  };
  n_units: number;
  max_tasks: number;
  counts: Counts;
  summary: Summary | null;
  baseline_id: string | null;
  finalized_at: string | null;
  mlflow_run_id: string | null;
  mlflow_url: string | null;
}

export interface Component {
  name: string;
  stage: string;
  kind: string;
  tests: string[];
  truth_available: Record<string, string>;
  models: string[];
}

export interface Gate {
  component: string;
  baseline_id: string | null;
  experiments: { id: string; status: string; purpose: string; floors_pass: boolean | null }[];
  report: string;
}

export interface SubmitBody {
  component: string;
  overrides?: Record<string, unknown>;
  suites?: string[];
  datasets?: string[];
  devices?: string[];
  baseline_id?: string;
  purpose?: string;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authorizedFetch(`${API_BASE}${path}`, {
    ...init,
    headers: { ...(init.body ? { 'Content-Type': 'application/json' } : {}), ...(init.headers ?? {}) },
  });
  if (res.status === 401) throw new AuthError(401, 'Not signed in');
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') message = body.detail;
    } catch {
      /* keep the generic message */
    }
    throw new AuthError(res.status, message);
  }
  return res.json() as Promise<T>;
}

export const listExperiments = (component?: string) =>
  call<Experiment[]>(`/experiments?limit=100${component ? `&component=${encodeURIComponent(component)}` : ''}`);
export const getExperiment = (id: string) => call<Experiment>(`/experiments/${id}`);
export const listComponents = () => call<Component[]>('/experiments/components');
export const getGate = (component: string) => call<Gate>(`/experiments/gate/${encodeURIComponent(component)}`);
export const submitExperiment = (body: SubmitBody) =>
  call<{ id: string; n_units: number }>('/experiments', { method: 'POST', body: JSON.stringify(body) });
export const cancelExperiment = (id: string) => call(`/experiments/${id}/cancel`, { method: 'POST' });
export const retryFailed = (id: string) =>
  call<{ republished: number }>(`/experiments/${id}/retry-failed`, { method: 'POST' });
export const pinBaseline = (id: string) => call(`/experiments/${id}/pin-baseline`, { method: 'POST' });
